/**
 * Note Component
 *
 * @component
 * @description This component represents a note and provides an interface to view, edit, and delete it.
 * It displays the note's content, author, creation date, and provides actions to edit or delete the note.
 *
 * @param {NoteItemProps} props - The properties and event handlers required by the component.
 * @returns {JSX.Element} - A JSX element representing the Note component.
 */

import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import useNotesContext from 'context/NotesContext';
import useDebounce from '../../../hooks/useDebounce/useDebounce';
import { NoteData, NoteItemProps } from '../types';
import NoteEdit from './NoteEdit';

const Note = ({ note, isDisabled }: NoteItemProps) => {
  const { onUpdateNotes } = useNotesContext();
  const { content } = note;
  const [noteContent, setNoteContent] = useState<string>(content);

  /**
   * @function
   * @name handleOnSaveSingleNote
   * @description Saves changes made to a note.
   * @return {void}
   */
  const handleOnSaveSingleNote = useCallback(() => {
    const noteData: NoteData = {
      ...note,
      isNew: false,
      content: noteContent,
    };
    onUpdateNotes?.(noteData);
  }, [note, noteContent, onUpdateNotes]);

  /**
   * @function
   * @name handleOnChangeContentNote
   * @description Handles changes in the content of a note.
   * @param {ChangeEvent} event - The change event that occurred when the content of the note input field changed.
   * @return {void}
   */
  const handleOnChangeContentNote = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setNoteContent(event.target.value);
  }, []);

  const debouncedSaveNote = useDebounce(handleOnSaveSingleNote, 250);

  useEffect(() => {
    if (noteContent) {
      debouncedSaveNote();
    }
  }, [noteContent, debouncedSaveNote]);

  return (
    <NoteEdit noteContent={noteContent} isDisabled={isDisabled} handleOnChangeContentNote={handleOnChangeContentNote} />
  );
};

export default Note;
